// Lieux, tableaux et catalogue des profils : schémas de `/api/locations`, `/api/tables`,
// `/api/templates` et `/api/categories`, communs au serveur et au front (écrans Lieux et Création).
import { z } from 'zod';
import type { NAVIGATION_ADVISORY } from './links.ts';
import type { Category, HintResult, Season, TemplateRole, WindowStatus } from './templates/index.ts';

export const SEA_ORIENTATIONS = ['N', 'NE', 'E', 'SE', 'S', 'SO', 'O', 'NO'] as const;
export type SeaOrientation = (typeof SEA_ORIENTATIONS)[number];

const name = z.string().trim().min(1).max(100);

/** Quotas par compte : chaque lieu nouveau peut coûter une maille de plus à la synchro. */
export const MAX_LOCATIONS_PER_USER = 200;
export const MAX_TABLES_PER_USER = 50;

const locationFields = {
  name,
  lat: z.number().min(-90).max(90),
  lon: z.number().min(-180).max(180),
  /** Spot côtier : côté où se trouve la mer (vent de terre / de mer, houle). `null` : pas côtier. */
  seaOrientation: z.enum(SEA_ORIENTATIONS).nullable(),
  /** Privé par défaut : un lieu partagé est visible (coordonnées comprises) par tous les comptes. */
  isPublic: z.boolean(),
};

export const locationInputSchema = z.object({ ...locationFields, seaOrientation: locationFields.seaOrientation.default(null), isPublic: locationFields.isPublic.default(false) });

/** Modification : sans valeurs par défaut, un champ absent reste inchangé. */
export const locationPatchSchema = z
  .object(locationFields)
  .partial()
  .refine((input) => Object.keys(input).length > 0, { error: 'Aucune modification demandée' });

export interface LocationDto {
  id: number;
  name: string;
  lat: number;
  lon: number;
  /** Lieu partagé par un autre compte : position de sa maille (~1 km), pas les coordonnées exactes. */
  approximate: boolean;
  seaOrientation: SeaOrientation | null;
  isPublic: boolean;
  /** Lieu du compte (modifiable) ; sinon lieu public d'un autre compte ou préchargé. */
  mine: boolean;
}

/** Bornes de bon sens d'une limite personnelle (nœuds, mètres, secondes). */
export const MAX_PARAM_VALUE = 100;

export interface ParamBounds {
  label: string;
  min: number;
  max: number;
  step: number;
  /** Unité affichée après la valeur (« nds », « m », « s »). */
  unit: string;
}

/**
 * Bornes des limites personnelles, par poste ou esche (slug) puis par limite : curseurs du front
 * et contrôle du serveur. Propres à chaque poste, sinon un kayakiste aurait les plafonds du
 * bateau. Une limite absente de cette liste n'est bornée que par MAX_PARAM_VALUE. Plafonds
 * revus avec les sources de `docs/revue-templates.md` (ENVSN, FFCK, D240, SNSM). Limites
 * d'esche préfixées par son slug : pas de nom commun avec un poste.
 */
export const PARAM_BOUNDS: Record<string, Record<string, ParamBounds>> = {
  kayak: {
    vent_max: { label: 'Vent moyen max', min: 5, max: 16, step: 1, unit: 'nds' },
    rafales_max: { label: 'Rafales max', min: 8, max: 22, step: 1, unit: 'nds' },
    vent_terre_max: { label: 'Vent de terre max', min: 0, max: 8, step: 1, unit: 'nds' },
    houle_max: { label: 'Mer max', min: 0.2, max: 1.5, step: 0.1, unit: 'm' },
  },
  bateau: {
    vent_max: { label: 'Vent moyen max', min: 5, max: 21, step: 1, unit: 'nds' },
    rafales_max: { label: 'Rafales max', min: 8, max: 27, step: 1, unit: 'nds' },
    vent_terre_max: { label: 'Vent de terre max', min: 0, max: 15, step: 1, unit: 'nds' },
    houle_max: { label: 'Mer max', min: 0.2, max: 2, step: 0.1, unit: 'm' },
    periode_min: { label: 'Période min', min: 3, max: 8, step: 1, unit: 's' },
  },
  leurre: {
    leurre_ecart_crepuscule_max: { label: 'Écart à l’aube ou au crépuscule', min: 0, max: 3, step: 1, unit: 'h' },
  },
};

/** Bornes d'une limite pour un poste ou une esche (slug), si elles sont connues. */
export function paramBounds(slug: string, name: string): ParamBounds | undefined {
  return PARAM_BOUNDS[slug]?.[name];
}

/** Problème d'une valeur de limite d'un poste ou d'une esche, ou `null` si elle est dans ses bornes. */
export function paramProblem(slug: string, name: string, value: number): string | null {
  const bounds = paramBounds(slug, name);
  if (!bounds) return null;
  if (value < bounds.min || value > bounds.max) return `${bounds.label} : entre ${bounds.min} et ${bounds.max} ${bounds.unit}`;
  return null;
}

const profileSchema = z.object({
  templateId: z.number().int().positive(),
  /** Pêche : poste composé avec l'espèce. */
  posteId: z.number().int().positive().nullable().default(null),
  /** Pêche : esche (leurre) composée avec l'espèce. */
  escheId: z.number().int().positive().nullable().default(null),
  /** Limites personnelles du poste et de l'esche (ex. `{ "vent_max": 12 }`). */
  params: z.record(z.string(), z.number().min(0).max(MAX_PARAM_VALUE)).nullable().default(null),
});

export const tableInputSchema = z.object({
  name,
  profiles: z.array(profileSchema).min(1).max(50),
  locationIds: z.array(z.number().int().positive()).min(1).max(50),
});

/** Modification : profils et lieux remplacés en entier s'ils sont fournis. */
export const tablePatchSchema = tableInputSchema.partial().refine((input) => Object.keys(input).length > 0, { error: 'Aucune modification demandée' });

export interface TableProfileDto {
  templateId: number;
  slug: string;
  name: string;
  category: Category;
  posteId: number | null;
  posteSlug: string | null;
  escheId: number | null;
  escheSlug: string | null;
  params: Record<string, number> | null;
}

export interface TableDto {
  id: number;
  name: string;
  createdAt: string;
  profiles: TableProfileDto[];
  locations: { id: number; name: string }[];
}

/** Profil proposé à la création d'un tableau : dernière version publiée, non abstraite. */
export interface CatalogTemplateDto {
  id: number;
  slug: string;
  name: string;
  category: Category;
  role: TemplateRole | null;
  navigation: boolean;
  /** Saison (un poste ou une esche vaut toute l'année). */
  season: Season;
  /** Poste ou esche : limites personnelles réglables, avec leur valeur par défaut. */
  params: { name: string; default: number }[];
}

/** Fenêtre d'un tableau du compte (« Mes fenêtres »). */
export interface MyWindowDto {
  tableId: number;
  tableName: string;
  location: { id: number; name: string };
  /** Pêche : `species`, `poste` et `esche` (noms) séparés pour le regroupement « par espèce ». */
  profile: { slug: string; name: string; species: string; poste: string | null; esche: string | null; category: Category };
  status: WindowStatus;
  start: string | null;
  end: string | null;
  startHour: number | null;
  endHour: number | null;
  score: number | null;
  /** Saison du profil (affichée hors saison). */
  season: Season | null;
  advisory: typeof NAVIGATION_ADVISORY | null;
  /** Indices actifs (« Fortes pluies récentes (85 mm sur 10 j) … »), affichage seul. */
  hints: HintResult[];
}

export interface MyWindowsDto {
  /** Dernier calcul des fenêtres (ISO), `null` s'il n'y en a pas encore. */
  updatedAt: string | null;
  windows: MyWindowDto[];
}
